import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Printer, ScanLine, X } from "lucide-react";
import { toast } from "sonner";
import { formatVariantLabel, sortVariants } from "@/lib/variants";

/**
 * BarcodeLabelDialog — PDF etiket yazdırma sihirbazı.
 *
 * Yeni:
 *  · Tarama modu (barkod okuyucu ile hızlı seçim)
 *  · SKU kolonu gizli; Beden · Renk yeşil renkte gösterilir
 *  · Varsayılan olarak hiçbir varyant seçili değildir
 */
export default function BarcodeLabelDialog({
  open, onOpenChange, productId, variants: rawVariants = [], companyName = "",
}) {
  // Program genelinde varyantlar önce renk sonra beden sırasında listelenir.
  const variants = useMemo(() => sortVariants(rawVariants || []), [rawVariants]);
  const [presets, setPresets] = useState([]);
  const [presetKey, setPresetKey] = useState("a4_3x8_70x37");
  const [defaultCopies, setDefaultCopies] = useState(1);
  const [selected, setSelected] = useState({}); // variant_id -> copies (int)
  const [showName, setShowName] = useState(true);
  const [showBarcodeNumber, setShowBarcodeNumber] = useState(true);
  const [showAttrs, setShowAttrs] = useState(true);
  const [showPrice, setShowPrice] = useState(false);
  const [company, setCompany] = useState(companyName);

  // Özel ebat modu
  const [customMode, setCustomMode] = useState(false);
  const [customType, setCustomType] = useState("sheet");
  const [customW, setCustomW] = useState(70);
  const [customH, setCustomH] = useState(37);
  const [customCols, setCustomCols] = useState(3);
  const [customRows, setCustomRows] = useState(8);
  const [customMarginL, setCustomMarginL] = useState(7.5);
  const [customMarginT, setCustomMarginT] = useState(15);
  const [customGapX, setCustomGapX] = useState(2.5);
  const [customGapY, setCustomGapY] = useState(0);
  const [generating, setGenerating] = useState(false);

  // Tarama modu
  const [scanMode, setScanMode] = useState(false);
  const [scanValue, setScanValue] = useState("");
  const [lastScanned, setLastScanned] = useState(null); // {label, ok}
  const scanRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    api.get("/barcodes/labels/presets")
      .then(({ data }) => setPresets(data.presets || []))
      .catch(() => setPresets([]));
    // Kullanıcı isteği: varsayılan olarak hiçbir varyant seçili gelmesin.
    setSelected({});
    setDefaultCopies(1);
    setCustomMode(false);
    setScanMode(false);
    setScanValue("");
    setLastScanned(null);
  }, [open, variants]);

  // Tarama modu açıldığında input'a odaklan
  useEffect(() => {
    if (scanMode && open) {
      const t = setTimeout(() => scanRef.current?.focus(), 100);
      return () => clearTimeout(t);
    }
  }, [scanMode, open]);

  const selectedList = useMemo(
    () => Object.entries(selected).filter(([, c]) => Number(c) > 0),
    [selected],
  );
  const totalLabels = useMemo(
    () => selectedList.reduce((acc, [, c]) => acc + Number(c || 0), 0),
    [selectedList],
  );

  const setCopies = (vid, n) => {
    const v = Math.max(0, Math.min(500, Number(n) || 0));
    setSelected((p) => ({ ...p, [vid]: v }));
  };

  const toggleAll = (checked) => {
    if (checked) {
      const next = {};
      variants.forEach((v) => {
        if (v.barcode || v.sku) next[v.id] = defaultCopies || 1;
      });
      setSelected(next);
    } else {
      setSelected({});
    }
  };

  const applyBulk = () => {
    const next = {};
    variants.forEach((v) => {
      if (v.barcode || v.sku) next[v.id] = defaultCopies;
    });
    setSelected(next);
  };

  const currentPreset = useMemo(
    () => presets.find((p) => p.key === presetKey),
    [presets, presetKey],
  );

  // ---- Tarama modu — barkod okuyucudan gelen kodu işle ---------------------
  const handleScanSubmit = (raw) => {
    const code = (raw || "").trim();
    if (!code) return;
    // Barkod okuyucular kodun sonuna Enter (ve bazen ön/arka boşluk) ekler.
    const found = variants.find(
      (v) => (v.barcode && v.barcode === code) || (v.sku && v.sku === code),
    );
    if (found) {
      // Her okutmada +1 kopya (paketleme akışı için ideal)
      setSelected((p) => ({ ...p, [found.id]: Number(p[found.id] || 0) + 1 }));
      const attrs = formatVariantLabel(found.attributes, found.sku || "");
      setLastScanned({ ok: true, label: `${attrs} · +1 kopya` });
      // Kısa uyarı sesi (browser blocking olabilir; sessizce görmezden geliyoruz)
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const o = ctx.createOscillator();
        o.frequency.value = 880;
        o.connect(ctx.destination);
        o.start(); o.stop(ctx.currentTime + 0.05);
      } catch (_) {}
    } else {
      setLastScanned({ ok: false, label: `Eşleşme yok: ${code}` });
    }
    setScanValue("");
    // Odağı input'ta tut
    setTimeout(() => scanRef.current?.focus(), 10);
  };

  const download = async () => {
    if (totalLabels <= 0) {
      toast.error("En az bir varyant için adet girmelisiniz");
      return;
    }
    setGenerating(true);
    try {
      const body = {
        preset: customMode ? null : presetKey,
        default_copies: 1,
        variants: selectedList.map(([variant_id, copies]) => ({
          variant_id, copies: Number(copies),
        })),
        show_name: showName,
        show_barcode_number: showBarcodeNumber,
        show_attributes: showAttrs,
        show_price: showPrice,
        company_name: company || "",
      };
      if (customMode) {
        body.layout_type = customType;
        body.custom = {
          label_w: Number(customW), label_h: Number(customH),
        };
        if (customType === "sheet") {
          body.custom.cols = Number(customCols);
          body.custom.rows = Number(customRows);
          body.custom.margin_left = Number(customMarginL);
          body.custom.margin_top = Number(customMarginT);
          body.custom.gap_x = Number(customGapX);
          body.custom.gap_y = Number(customGapY);
        }
      }
      const resp = await api.post(
        `/products/${productId}/barcodes/labels/pdf`,
        body,
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      // Yeni sekmede aç (indirme yok)
      const win = window.open(url, "_blank");
      if (!win) {
        toast.error("Pop-up engellendi — lütfen tarayıcı ayarından bu siteye izin verin");
      } else {
        toast.success(`${totalLabels} etiket yeni sekmede açıldı`);
      }
      // Belleği yaklaşık 60sn sonra serbest bırak (yeni sekme yüklenene kadar)
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="label-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Printer className="w-4 h-4 text-blue-400" /> Barkod Etiketi Yazdır (PDF)
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Yazdırmak istediğiniz varyantları işaretleyin veya <b>Tarama Modu</b>'nu açıp barkod okuyucu ile hızlı seçim yapın. Barkodlar EAN-13 uyumludur.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Sol: Şablon + görünüm */}
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase font-mono text-muted-foreground">
                Etiket Boyutu / Şablon
              </Label>
              <div className="mt-1 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setCustomMode(false)}
                  className={`text-[11px] font-mono uppercase tracking-widest px-2 py-1 border rounded-sm ${
                    !customMode ? "border-blue-500 bg-blue-950/30 text-blue-300" : "border-border text-slate-400"
                  }`}
                  data-testid="label-preset-mode"
                >
                  Hazır
                </button>
                <button
                  type="button"
                  onClick={() => setCustomMode(true)}
                  className={`text-[11px] font-mono uppercase tracking-widest px-2 py-1 border rounded-sm ${
                    customMode ? "border-blue-500 bg-blue-950/30 text-blue-300" : "border-border text-slate-400"
                  }`}
                  data-testid="label-custom-mode"
                >
                  Özel Ebat
                </button>
              </div>
            </div>

            {!customMode && (
              <div>
                <Select value={presetKey} onValueChange={setPresetKey}>
                  <SelectTrigger className="rounded-sm" data-testid="label-preset-select">
                    <SelectValue placeholder="Şablon seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {presets.map((p) => (
                      <SelectItem key={p.key} value={p.key}>
                        {p.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {currentPreset && (
                  <div className="mt-2 text-[11px] font-mono text-muted-foreground">
                    {currentPreset.label_w} × {currentPreset.label_h} mm ·{" "}
                    {currentPreset.type === "sheet"
                      ? `${currentPreset.cols}×${currentPreset.rows} = ${currentPreset.cols * currentPreset.rows}/sayfa`
                      : "Termal rulo (1 etiket/sayfa)"}
                  </div>
                )}
              </div>
            )}

            {customMode && (
              <div className="space-y-2 border border-border rounded-sm p-3 bg-muted/20">
                <div className="flex gap-2 items-center">
                  <Label className="text-xs">Tip</Label>
                  <Select value={customType} onValueChange={setCustomType}>
                    <SelectTrigger className="rounded-sm h-8 w-40" data-testid="label-custom-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sheet">A4 Sayfa Etiketi</SelectItem>
                      <SelectItem value="roll">Termal Rulo (1/sayfa)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Genişlik (mm)</Label>
                    <Input type="number" step="0.1" min="10" value={customW}
                      onChange={(e) => setCustomW(e.target.value)}
                      className="rounded-sm h-8 mt-1 font-mono" />
                  </div>
                  <div>
                    <Label className="text-xs">Yükseklik (mm)</Label>
                    <Input type="number" step="0.1" min="10" value={customH}
                      onChange={(e) => setCustomH(e.target.value)}
                      className="rounded-sm h-8 mt-1 font-mono" />
                  </div>
                </div>
                {customType === "sheet" && (
                  <>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Sütun</Label>
                        <Input type="number" min="1" max="10" value={customCols}
                          onChange={(e) => setCustomCols(e.target.value)}
                          className="rounded-sm h-8 mt-1 font-mono" />
                      </div>
                      <div>
                        <Label className="text-xs">Satır</Label>
                        <Input type="number" min="1" max="20" value={customRows}
                          onChange={(e) => setCustomRows(e.target.value)}
                          className="rounded-sm h-8 mt-1 font-mono" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Üst Boşluk (mm)</Label>
                        <Input type="number" step="0.1" min="0" value={customMarginT}
                          onChange={(e) => setCustomMarginT(e.target.value)}
                          className="rounded-sm h-8 mt-1 font-mono" />
                      </div>
                      <div>
                        <Label className="text-xs">Sol Boşluk (mm)</Label>
                        <Input type="number" step="0.1" min="0" value={customMarginL}
                          onChange={(e) => setCustomMarginL(e.target.value)}
                          className="rounded-sm h-8 mt-1 font-mono" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Yatay Boşluk (mm)</Label>
                        <Input type="number" step="0.1" min="0" value={customGapX}
                          onChange={(e) => setCustomGapX(e.target.value)}
                          className="rounded-sm h-8 mt-1 font-mono" />
                      </div>
                      <div>
                        <Label className="text-xs">Dikey Boşluk (mm)</Label>
                        <Input type="number" step="0.1" min="0" value={customGapY}
                          onChange={(e) => setCustomGapY(e.target.value)}
                          className="rounded-sm h-8 mt-1 font-mono" />
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            <div className="border border-border rounded-sm p-3 bg-muted/10 space-y-2">
              <Label className="text-xs uppercase font-mono text-muted-foreground">
                Etikette Göster
              </Label>
              <div className="flex flex-wrap gap-3 text-xs">
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <Checkbox checked={showName} onCheckedChange={setShowName} data-testid="label-show-name" />
                  Ürün Adı
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <Checkbox checked={showAttrs} onCheckedChange={setShowAttrs} data-testid="label-show-attrs" />
                  Renk · Beden
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <Checkbox checked={showBarcodeNumber} onCheckedChange={setShowBarcodeNumber} data-testid="label-show-barcode-number" />
                  Barkod Numarası
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <Checkbox checked={showPrice} onCheckedChange={setShowPrice} data-testid="label-show-price" />
                  Satış Fiyatı
                </label>
              </div>
              <div>
                <Label className="text-xs">Üst Yazı (firma/marka)</Label>
                <Input value={company} onChange={(e) => setCompany(e.target.value)}
                  placeholder="örn: Fucan Personel Giyim"
                  className="rounded-sm h-8 mt-1" data-testid="label-company" />
              </div>
            </div>
          </div>

          {/* Sağ: Varyantlar + Tarama Modu */}
          <div className="space-y-2">
            {/* Tarama modu paneli */}
            <div className={`border rounded-sm ${scanMode ? "border-emerald-600 bg-emerald-950/20" : "border-border bg-muted/10"}`}>
              <button
                type="button"
                onClick={() => setScanMode((s) => !s)}
                className={`w-full flex items-center justify-between px-3 py-2 text-xs uppercase tracking-widest font-mono ${
                  scanMode ? "text-emerald-400" : "text-muted-foreground hover:text-slate-200"
                }`}
                data-testid="scan-mode-toggle"
              >
                <span className="flex items-center gap-1.5">
                  <ScanLine className="w-3.5 h-3.5" />
                  Tarama Modu {scanMode ? "· AÇIK" : "(barkod okuyucu ile hızlı seçim)"}
                </span>
                <span className="text-[10px]">{scanMode ? "kapat" : "aç"}</span>
              </button>
              {scanMode && (
                <div className="px-3 pb-3 space-y-2">
                  <Input
                    ref={scanRef}
                    value={scanValue}
                    onChange={(e) => setScanValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleScanSubmit(scanValue);
                      }
                    }}
                    onBlur={() => setTimeout(() => scanRef.current?.focus(), 200)}
                    placeholder="Barkodu okutun veya elle yazın, Enter'a basın…"
                    className="rounded-sm h-9 font-mono text-sm border-emerald-700"
                    data-testid="scan-input"
                    autoFocus
                  />
                  {lastScanned && (
                    <div className={`text-[11px] font-mono ${lastScanned.ok ? "text-emerald-400" : "text-rose-400"}`}
                      data-testid="scan-last-result"
                    >
                      → {lastScanned.label}
                    </div>
                  )}
                  <div className="text-[10px] text-muted-foreground">
                    İpucu: Barkod okuyucular otomatik Enter gönderir. Her okutmada seçili adet +1 artar.
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase font-mono text-muted-foreground">
                Varyantlar ({variants.length})
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number" min="0" max="500" value={defaultCopies}
                  onChange={(e) => setDefaultCopies(Number(e.target.value) || 0)}
                  className="rounded-sm h-7 w-16 text-xs font-mono text-right"
                  title="Toplu adet"
                  data-testid="label-bulk-copies"
                />
                <button type="button" onClick={applyBulk}
                  className="text-[10px] uppercase tracking-widest font-mono text-blue-500 hover:text-blue-400"
                  data-testid="label-apply-bulk"
                >
                  Hepsine uygula
                </button>
                <button type="button" onClick={() => toggleAll(false)}
                  className="text-[10px] uppercase tracking-widest font-mono text-slate-500 hover:text-slate-300"
                  data-testid="label-clear-selection"
                >
                  Temizle
                </button>
              </div>
            </div>
            <div className="border border-border rounded-sm max-h-72 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted/40 sticky top-0">
                  <tr className="text-[10px] uppercase font-mono text-muted-foreground">
                    <th className="text-left px-2 py-1.5 w-8">✓</th>
                    <th className="text-left px-2 py-1.5">Renk · Beden</th>
                    <th className="text-left px-2 py-1.5">Barkod</th>
                    <th className="text-right px-2 py-1.5 w-16">Adet</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {variants.length === 0 && (
                    <tr>
                      <td colSpan={4} className="text-center py-6 text-muted-foreground">
                        Varyant yok
                      </td>
                    </tr>
                  )}
                  {variants.map((v) => {
                    const printable = !!(v.barcode || v.sku);
                    const attrs = formatVariantLabel(v.attributes, "—");
                    const copies = Number(selected[v.id] || 0);
                    return (
                      <tr key={v.id} data-testid={`label-row-${v.id}`}
                        className={!printable ? "opacity-40" : ""}>
                        <td className="px-2 py-1">
                          <Checkbox
                            checked={copies > 0}
                            disabled={!printable}
                            onCheckedChange={(checked) => setCopies(v.id, checked ? (defaultCopies || 1) : 0)}
                            data-testid={`label-check-${v.id}`}
                          />
                        </td>
                        <td className="px-2 py-1 font-semibold text-emerald-500 dark:text-emerald-400">
                          {attrs || "—"}
                        </td>
                        <td className="px-2 py-1 font-mono">
                          {v.barcode ? (
                            <span className="text-slate-300">{v.barcode}</span>
                          ) : (
                            <span className="text-[10px] text-amber-500">Barkod yok</span>
                          )}
                        </td>
                        <td className="px-2 py-1">
                          <Input
                            type="number" min="0" max="500"
                            value={copies}
                            disabled={!printable}
                            onChange={(e) => setCopies(v.id, e.target.value)}
                            className="rounded-sm h-7 w-14 text-xs font-mono text-right"
                            data-testid={`label-copies-${v.id}`}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="text-[11px] font-mono text-muted-foreground">
              Toplam: <b className="text-blue-400">{totalLabels}</b> etiket
              {" · "}
              <b className="text-slate-300">{selectedList.length}</b> farklı varyant
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-sm">
            İptal
          </Button>
          <Button onClick={download} disabled={generating || totalLabels === 0}
            className="rounded-sm bg-blue-600 hover:bg-blue-500 disabled:opacity-40"
            data-testid="label-download-btn"
          >
            {generating ? (
              <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Üretiliyor</>
            ) : (
              <><Printer className="w-3.5 h-3.5 mr-1" /> PDF Yazdır ({totalLabels})</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
